import { useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import type { ManualCheckView, SupportedLocale } from '@stm/contracts';
import { translate } from '@stm/i18n';
import { apiRequest } from './api';
import { formatCalendarDate } from './athletes';
import { errorKeyOf, type ErrorKey } from './BillingForms';
import {
  manualCheckPath,
  recordManualCheckRequest,
  withdrawManualCheckRequest,
} from './manualChecks';

export interface ManualCheckItemProps {
  locale: SupportedLocale;
  base: string;
  csrfToken: string;
  matchId: string;
  check: ManualCheckView;
  /** Holds `competitions.manage` here and the match is not cancelled. */
  canRecord: boolean;
  /** Reloads the eligibility view after a record or a withdrawal. */
  onChanged: () => Promise<void>;
}

/**
 * One mandatory manual check of the active rule version: open, or recorded as done with the written
 * statement and the day it was checked. A registrar records it or withdraws the record with a reason.
 */
export function ManualCheckItem({
  locale,
  base,
  csrfToken,
  matchId,
  check,
  canRecord,
  onChanged,
}: ManualCheckItemProps): React.JSX.Element {
  const [mode, setMode] = useState<'record' | 'withdraw' | null>(null);
  const [statement, setStatement] = useState('');
  const [checkedOn, setCheckedOn] = useState('');
  const [reason, setReason] = useState('');
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [busy, setBusy] = useState(false);

  const recordRequest = recordManualCheckRequest(statement, checkedOn);
  const withdrawRequest = withdrawManualCheckRequest(reason);

  function close(): void {
    setMode(null);
    setStatement('');
    setCheckedOn('');
    setReason('');
    setErrorKey(null);
  }

  async function submit(): Promise<void> {
    setBusy(true);
    setErrorKey(null);
    try {
      if (mode === 'record' && recordRequest !== null) {
        await apiRequest<ManualCheckView>(manualCheckPath(base, matchId, check.key, false), {
          method: 'PUT',
          body: recordRequest,
          csrfToken,
        });
      } else if (mode === 'withdraw' && withdrawRequest !== null && check.record !== null) {
        await apiRequest<ManualCheckView>(manualCheckPath(base, matchId, check.key, true), {
          method: 'POST',
          body: withdrawRequest,
          csrfToken,
          ifMatchVersion: check.record.version,
        });
      } else {
        return;
      }
      close();
      await onChanged();
    } catch (error) {
      setErrorKey(errorKeyOf(error));
      await onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box component="li" sx={{ mb: 1 }}>
      <Typography variant="body2" component="span">
        {check.sourceClause}
        {' · '}
        {check.record === null
          ? translate(locale, 'manualCheck.open')
          : translate(locale, 'manualCheck.done').replace(
              '{checkedOn}',
              formatCalendarDate(check.record.checkedOn, locale),
            )}
      </Typography>
      {check.record !== null && (
        <Typography variant="body2" color="text.secondary">
          {check.record.statement}
        </Typography>
      )}
      {errorKey !== null && (
        <Alert severity="error" sx={{ mt: 0.5 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {canRecord && mode === null && (
        <Button
          size="small"
          sx={{ display: 'block', mt: 0.5 }}
          onClick={() => {
            setMode(check.record === null ? 'record' : 'withdraw');
          }}
        >
          {translate(locale, check.record === null ? 'manualCheck.record' : 'manualCheck.withdraw')}
        </Button>
      )}
      {canRecord && mode !== null && (
        <Stack
          component="form"
          spacing={1.5}
          sx={{ maxWidth: 480, mt: 1 }}
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          {mode === 'record' ? (
            <>
              <TextField
                required
                multiline
                minRows={2}
                size="small"
                label={translate(locale, 'manualCheck.statement')}
                helperText={translate(locale, 'manualCheck.statement.hint')}
                value={statement}
                onChange={(event) => {
                  setStatement(event.target.value);
                }}
                slotProps={{ htmlInput: { maxLength: 500 } }}
              />
              <TextField
                required
                size="small"
                type="date"
                label={translate(locale, 'manualCheck.checkedOn')}
                value={checkedOn}
                onChange={(event) => {
                  setCheckedOn(event.target.value);
                }}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </>
          ) : (
            <TextField
              required
              multiline
              minRows={2}
              size="small"
              label={translate(locale, 'manualCheck.withdraw.reason')}
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
              }}
              slotProps={{ htmlInput: { maxLength: 500 } }}
            />
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              type="submit"
              variant="contained"
              size="small"
              disabled={
                busy || (mode === 'record' ? recordRequest === null : withdrawRequest === null)
              }
            >
              {translate(
                locale,
                mode === 'record' ? 'manualCheck.record.submit' : 'manualCheck.withdraw.submit',
              )}
            </Button>
            <Button size="small" onClick={close}>
              {translate(locale, 'manualCheck.close')}
            </Button>
          </Box>
        </Stack>
      )}
    </Box>
  );
}
